import React, { useState, useEffect, useRef } from 'react';
import '../styles/InMeetingAssistant.css';
import { transcriptionService } from '../services/transcriptionService';
import { geminiService } from '../services/geminiService';
import Toast from './Toast';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import { io, Socket } from 'socket.io-client';

interface TranscriptSegment {
    id: string;
    speaker: string;
    text: string;
    timestamp: number;
    isFinal: boolean;
}

interface NotesData {
    summary: string;
    highlights: string;
    actionItems: string;
}

interface ModeDataEntry {
    transcript: TranscriptSegment[];
    notes: NotesData;
    duration: number;
}

interface ModeData {
    manual: ModeDataEntry;
    upload: ModeDataEntry;
    bot: ModeDataEntry;
}

const MOCK_CONVERSATION = [
    { speaker: 'Product Manager', text: "Alright, let's get started. Today we need to finalize the requirements for the new dashboard feature." },
    { speaker: 'Tech Lead', text: "I've reviewed the initial specs. The data visualization part looks heavy. Do we really need real-time updates?" },
    { speaker: 'Product Manager', text: "Yes, the users need to see the metrics update as they happen. It's a key differentiator." },
    { speaker: 'Designer', text: "From a UX perspective, we can use smooth transitions so it doesn't feel jittery." },
    { speaker: 'Tech Lead', text: "Okay, we can use WebSockets for that. But we'll need to cache the historical data efficiently." },
    { speaker: 'Business Analyst', text: "I'll update the acceptance criteria to include the real-time latency requirements. < 200ms?" },
    { speaker: 'Product Manager', text: "That sounds right. Also, let's make sure we have a fallback for poor connections." },
    { speaker: 'Tech Lead', text: "Agreed. We can show a 'last updated' timestamp if the socket disconnects." },
    { speaker: 'Designer', text: "I'll mock up the disconnected state. Should it be a banner or a subtle indicator?" },
    { speaker: 'Product Manager', text: "Subtle is better. Maybe a small dot in the corner. Red for offline, Green for online." },
    { speaker: 'Business Analyst', text: "Noted. I'll add a user story for the connection status indicator." },
    { speaker: 'Product Manager', text: "Great. What about the export functionality? Are we doing PDF and CSV?" },
    { speaker: 'Tech Lead', text: "CSV is easy. PDF might take more time if we need high-fidelity charts." },
    { speaker: 'Product Manager', text: "Let's prioritize CSV for MVP. PDF can be fast-follow." },
    { speaker: 'Business Analyst', text: "Okay, moving PDF export to Phase 2." }
];



interface InMeetingAssistantProps {
    initialNotes?: string;
    onNotesChange?: (notes: string) => void;
    onTranscriptChange?: (transcript: string) => void;
    onMoveActionItems?: (actionItems: string) => void;
}

const InMeetingAssistant: React.FC<InMeetingAssistantProps> = ({
    initialNotes = '',
    onNotesChange,
    onTranscriptChange,
    onMoveActionItems
}) => {
    const [mode, setMode] = useState<'select' | 'manual' | 'upload'>('select');
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

    // Separate state for each mode
    const [modeData, setModeData] = useState<ModeData>({
        manual: {
            transcript: [] as TranscriptSegment[],
            notes: { summary: '', highlights: '', actionItems: '' },
            duration: 0
        },
        upload: {
            transcript: [] as TranscriptSegment[],
            notes: { summary: '', highlights: '', actionItems: '' },
            duration: 0
        },
        bot: {
            transcript: [] as TranscriptSegment[],
            notes: { summary: '', highlights: '', actionItems: '' },
            duration: 0
        }
    });

    const [isRecording, setIsRecording] = useState(false);
    const [audioLevel, setAudioLevel] = useState<number[]>(new Array(5).fill(10));
    const [error, setError] = useState<string | null>(null);
    const [systemAudioCaptured, setSystemAudioCaptured] = useState<boolean>(false);
    const [botUrl, setBotUrl] = useState('');
    const [botStatus, setBotStatus] = useState<'idle' | 'joining' | 'connected' | 'completed'>('idle');
    const [transcriptionId, setTranscriptionId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isConverting, setIsConverting] = useState(false);

    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const botIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const segmentOffsetRef = useRef<number>(0); // Track segment count across recording sessions
    const socketRef = useRef<Socket | null>(null);

    // Initialize notes from props
    useEffect(() => {
        try {
            if (initialNotes && initialNotes.trim().startsWith('{')) {
                const parsed = JSON.parse(initialNotes);

                // Helper to parse transcript from string format
                const parseTranscript = (transcriptText: string): TranscriptSegment[] => {
                    if (!transcriptText) return [];
                    return transcriptText.split('\n').map((line, index) => {
                        const timeMatchWithUnix = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\|(\d+)\]\s*(.+)$/);
                        if (timeMatchWithUnix) {
                            return {
                                id: `seg-${index}`,
                                speaker: 'Speaker',
                                text: timeMatchWithUnix[3].trim(),
                                timestamp: parseInt(timeMatchWithUnix[2]),
                                isFinal: true
                            };
                        }
                        const timeMatch = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\]\s*(.+)$/);
                        if (timeMatch) {
                            return {
                                id: `seg-${index}`,
                                speaker: 'Speaker',
                                text: timeMatch[2].trim(),
                                timestamp: Date.now() - (index * 1000),
                                isFinal: true
                            };
                        }
                        return {
                            id: `seg-${index}`,
                            speaker: 'Speaker',
                            text: line.trim(),
                            timestamp: Date.now() - (index * 1000),
                            isFinal: true
                        };
                    }).filter(s => s.text.length > 0);
                };

                // Check if the parsed data has the new structure (with transcript + notes)
                const normalizeData = (data: any) => {
                    if (!data) return { transcript: [], notes: { summary: '', highlights: '', actionItems: '' } };

                    return {
                        transcript: data.transcript ? parseTranscript(data.transcript) : [],
                        notes: {
                            summary: data.notes?.summary || '',
                            highlights: data.notes?.highlights || '',
                            actionItems: data.notes?.actionItems || ''
                        }
                    };
                };

                setModeData(prev => ({
                    manual: { ...prev.manual, ...normalizeData(parsed.manual) },
                    upload: { ...prev.upload, ...normalizeData(parsed.upload) },
                    bot: { ...prev.bot, ...normalizeData(parsed.bot) }
                }));
            } else if (initialNotes) {
                // Plain text - could be a saved transcript
                // Convert plain text to transcript segments (split by newlines)
                const segments: TranscriptSegment[] = initialNotes.split('\n').map((line, index) => {
                    // Try to extract timestamp from format: [HH:MM|unixTimestamp] text
                    const timeMatchWithUnix = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\|(\d+)\]\s*(.+)$/);
                    if (timeMatchWithUnix) {
                        return {
                            id: `seg-${index}`,
                            speaker: 'Speaker',
                            text: timeMatchWithUnix[3].trim(),
                            timestamp: parseInt(timeMatchWithUnix[2]),
                            isFinal: true
                        };
                    }

                    // Fallback: Try old format [HH:MM] text (without Unix timestamp)
                    const timeMatch = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\]\s*(.+)$/);
                    if (timeMatch) {
                        return {
                            id: `seg-${index}`,
                            speaker: 'Speaker',
                            text: timeMatch[2].trim(),
                            timestamp: Date.now() - (index * 1000),
                            isFinal: true
                        };
                    }

                    // Fallback: use the whole line as text
                    return {
                        id: `seg-${index}`,
                        speaker: 'Speaker',
                        text: line.trim(),
                        timestamp: Date.now() - (index * 1000),
                        isFinal: true
                    };
                }).filter(s => s.text.length > 0);

                setModeData(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        transcript: segments.length > 0 ? segments : prev.manual.transcript
                        // Don't set notes here - keep them empty, transcripts should only go to transcript section
                    }
                }));
            }
        } catch (e) {
            // Fallback for plain text - convert to transcript
            if (initialNotes) {
                const segments: TranscriptSegment[] = initialNotes.split('\n').map((line, index) => {
                    // Try to extract timestamp from format: [HH:MM|unixTimestamp] text
                    const timeMatchWithUnix = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\|(\d+)\]\s*(.+)$/);
                    if (timeMatchWithUnix) {
                        return {
                            id: `seg-${index}`,
                            speaker: 'Speaker',
                            text: timeMatchWithUnix[3].trim(),
                            timestamp: parseInt(timeMatchWithUnix[2]),
                            isFinal: true
                        };
                    }

                    // Fallback: Try old format [HH:MM] text (without Unix timestamp)
                    const timeMatch = line.match(/^\[(\d{1,2}:\d{2}(?:\s?[AP]M)?)\]\s*(.+)$/);
                    if (timeMatch) {
                        return {
                            id: `seg-${index}`,
                            speaker: 'Speaker',
                            text: timeMatch[2].trim(),
                            timestamp: Date.now() - (index * 1000),
                            isFinal: true
                        };
                    }

                    // Fallback: use the whole line as text
                    return {
                        id: `seg-${index}`,
                        speaker: 'Speaker',
                        text: line.trim(),
                        timestamp: Date.now() - (index * 1000),
                        isFinal: true
                    };
                }).filter(s => s.text.length > 0);

                setModeData(prev => ({
                    ...prev,
                    manual: {
                        ...prev.manual,
                        transcript: segments.length > 0 ? segments : prev.manual.transcript
                        // Don't set notes here - keep them empty, transcripts should only go to transcript section
                    }
                }));
            }
        }
    }, [initialNotes]);

    // Helper to save complete data (transcript + notes) to parent
    const saveCompleteData = (data: typeof modeData) => {
        if (onNotesChange) {
            // Save complete structure with transcript and notes for each mode
            const completeData = {
                manual: {
                    transcript: data.manual.transcript
                        .filter(s => s.isFinal)
                        .map(s => {
                            const time = new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return `[${time}|${s.timestamp}] ${s.text}`;
                        })
                        .join('\n'),
                    notes: data.manual.notes
                },
                upload: {
                    transcript: data.upload.transcript
                        .filter(s => s.isFinal)
                        .map(s => {
                            const time = new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return `[${time}|${s.timestamp}] ${s.text}`;
                        })
                        .join('\n'),
                    notes: data.upload.notes
                },
                bot: {
                    transcript: data.bot.transcript
                        .filter(s => s.isFinal)
                        .map(s => {
                            const time = new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                            return `[${time}|${s.timestamp}] ${s.text}`;
                        })
                        .join('\n'),
                    notes: data.bot.notes
                }
            };
            onNotesChange(JSON.stringify(completeData));
        }
    };

    // Helper to update current mode's data
    const updateCurrentModeData = <K extends keyof ModeDataEntry>(
        key: K,
        value: ModeDataEntry[K]
    ) => {
        setModeData(prev => {
            if (mode === 'select') return prev;
            const currentMode = mode as keyof ModeData;

            const currentModeData = prev[currentMode];
            const newData = {
                ...prev,
                [currentMode]: { ...currentModeData, [key]: value }
            };

            // Save complete data whenever transcript or notes change
            if (key === 'notes' || key === 'transcript') {
                saveCompleteData(newData);
            }

            return newData;
        });
    };

    // Helper to auto-resize textarea based on content
    const autoResizeTextarea = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = event.target;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    };

    // Helper to update specific note section with auto-save
    const updateNoteSection = (section: 'summary' | 'highlights' | 'actionItems', value: string, event?: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (mode === 'select') return;

        // Auto-resize the textarea
        if (event) {
            autoResizeTextarea(event);
        }

        const currentNotes = modeData[mode].notes;
        const updatedNotes = { ...currentNotes, [section]: value };
        updateCurrentModeData('notes', updatedNotes);
        // Note: saveCompleteData is called automatically in updateCurrentModeData
    };

    // Move action items to post-meeting tab
    const handleMoveActionItemsToPost = () => {
        if (mode === 'select') return;

        const actionItems = modeData[mode].notes.actionItems;
        if (!actionItems || actionItems.trim().length === 0) {
            setToast({ message: 'No action items to move', type: 'info' });
            return;
        }

        // Call parent callback to handle moving action items
        if (onMoveActionItems) {
            onMoveActionItems(actionItems);
            setToast({ message: 'Action items copied to Post-Meeting tab', type: 'success' });
        } else {
            setToast({ message: 'Move action items feature not configured', type: 'error' });
        }
    };

    // Convert transcription to structured notes using Gemini AI
    const handleConvertToNotes = async () => {
        if (mode === 'select') return;

        const transcript = modeData[mode].transcript;
        if (transcript.length === 0) {
            setError('No transcription available to convert');
            setTimeout(() => setError(null), 3000);
            return;
        }

        setIsConverting(true);
        setError(null);

        try {
            // Format transcript as plain text
            const transcriptText = transcript
                .filter(s => s.isFinal)
                .map(s => {
                    const time = new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return `[${time}] ${s.text}`;
                })
                .join('\n');

            // Call Gemini service to convert
            const notes = await geminiService.convertTranscriptionToNotes(transcriptText);

            // Update the notes in current mode
            updateCurrentModeData('notes', notes);

            // Also trigger the parent's onNotesChange callback if provided
            if (onNotesChange) {
                const notesJson = JSON.stringify({
                    [mode]: notes
                });
                onNotesChange(notesJson);
            }

        } catch (err) {
            console.error('Error converting transcription:', err);
            setError(err instanceof Error ? err.message : 'Failed to convert transcription');
            setTimeout(() => setError(null), 5000);
        } finally {
            setIsConverting(false);
        }
    };

    // Getters for current mode data
    const currentTranscript = mode === 'select' ? [] : modeData[mode].transcript;
    const currentNotes = mode === 'select' ? { summary: '', highlights: '', actionItems: '' } : modeData[mode].notes;
    const currentDuration = mode === 'select' ? 0 : modeData[mode].duration;

    // Track isRecording in a ref so event handlers always have current value
    const isRecordingRef = useRef(isRecording);
    useEffect(() => {
        isRecordingRef.current = isRecording;
    }, [isRecording]);

    // Initialize Speech Recognition
    useEffect(() => {
        if (mode === 'manual') {
            if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
                const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
                recognitionRef.current = new SpeechRecognition();
                recognitionRef.current.continuous = true;
                recognitionRef.current.interimResults = true;
                recognitionRef.current.lang = 'en-US';

                recognitionRef.current.onresult = (event) => {
                    const results = event.results;

                    setModeData(prev => {
                        const prevTranscript = prev.manual.transcript;
                        const offset = segmentOffsetRef.current;

                        // Start with all existing segments
                        const updatedSegments = [...prevTranscript];

                        // Only process results starting from event.resultIndex (new results only)
                        for (let i = event.resultIndex; i < results.length; i++) {
                            const result = results[i];
                            const text = result[0].transcript.trim();
                            const isFinal = result.isFinal;

                            if (text) { // Only add non-empty text
                                const segmentId = `seg-${offset}-${i}`;

                                // Find if this segment already exists
                                const existingIndex = updatedSegments.findIndex(s => s.id === segmentId);

                                if (existingIndex !== -1) {
                                    // Update existing segment (interim -> final)
                                    if (isFinal) {
                                        updatedSegments[existingIndex] = {
                                            ...updatedSegments[existingIndex],
                                            text: text,
                                            isFinal: true
                                        };
                                    }
                                } else {
                                    // Add new segment
                                    updatedSegments.push({
                                        id: segmentId,
                                        speaker: 'Speaker',
                                        text: text,
                                        timestamp: Date.now(),
                                        isFinal: isFinal
                                    });
                                }
                            }
                        }

                        const updatedData = {
                            ...prev,
                            manual: {
                                ...prev.manual,
                                transcript: updatedSegments
                            }
                        };

                        // Save complete data (transcript + notes) automatically
                        saveCompleteData(updatedData);

                        return updatedData;
                    });
                };

                recognitionRef.current.onerror = (event) => {
                    console.error('Speech recognition error', event.error);
                    if (event.error === 'not-allowed') {
                        setError('Microphone access denied.');
                        stopRecording();
                    } else if (event.error === 'no-speech') {
                        // Don't show error for no speech, just continue listening
                        console.log('No speech detected, continuing...');
                    } else if (event.error === 'aborted') {
                        // Recognition was aborted, restart it if still recording
                        if (isRecordingRef.current && recognitionRef.current) {
                            try {
                                recognitionRef.current.start();
                            } catch (e) {
                                console.warn('Could not restart recognition:', e);
                            }
                        }
                    }
                };

                recognitionRef.current.onend = () => {
                    // Automatically restart if still recording
                    if (isRecordingRef.current && recognitionRef.current) {
                        try {
                            recognitionRef.current.start();
                            console.log('Speech recognition restarted');
                        } catch (e) {
                            console.warn('Could not restart recognition:', e);
                        }
                    }
                };
            } else {
                setError('Speech recognition not supported in this browser.');
            }
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, [mode]);

    // Timer effect
    useEffect(() => {
        if (isRecording || botStatus === 'connected') {
            timerRef.current = setInterval(() => {
                setModeData(prev => {
                    if (mode === 'select') return prev;
                    const currentMode = mode as keyof ModeData;

                    const currentDuration = prev[currentMode].duration;
                    return {
                        ...prev,
                        [currentMode]: { ...prev[currentMode], duration: currentDuration + 1 }
                    };
                });
            }, 1000);
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (botIntervalRef.current) clearInterval(botIntervalRef.current);
        };
    }, [isRecording, botStatus, mode]);

    // Visualize Audio
    const visualizeAudio = () => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        const bars = 5;
        const step = Math.floor(dataArray.length / bars);
        const newLevels = [];

        for (let i = 0; i < bars; i++) {
            let sum = 0;
            for (let j = 0; j < step; j++) {
                sum += dataArray[i * step + j];
            }
            const average = sum / step;
            newLevels.push(Math.max(4, (average / 255) * 100));
        }

        setAudioLevel(newLevels);
        animationFrameRef.current = requestAnimationFrame(visualizeAudio);
    };

    const saveCurrentTranscription = async () => {
        if (mode === 'select' || (!isRecording && botStatus !== 'connected')) return;

        setIsSaving(true);
        try {
            const currentData = modeData[mode];
            const id = await transcriptionService.saveTranscription(
                currentData.transcript,
                currentData.notes,
                currentData.duration,
                transcriptionId || undefined
            );
            if (id) setTranscriptionId(id);
        } catch (error) {
            console.error('Failed to save transcription:', error);
        } finally {
            setIsSaving(false);
        }
    };

    // Periodic saving
    useEffect(() => {
        let saveInterval: NodeJS.Timeout;
        if (isRecording || botStatus === 'connected') {
            saveInterval = setInterval(saveCurrentTranscription, 30000); // Save every 30 seconds
        }
        return () => {
            if (saveInterval) clearInterval(saveInterval);
        };
    }, [isRecording, botStatus, mode, modeData, transcriptionId]);

    const startRecording = async () => {
        try {
            // Get microphone stream
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Try to get system audio (tab audio from meeting)
            let systemStream: MediaStream | null = null;
            try {
                const displayStreamOptions: DisplayMediaStreamOptions = {
                    video: { cursor: 'always' } as MediaTrackConstraints,
                    audio: { echoCancellation: true, noiseSuppression: true }
                };
                systemStream = await (navigator.mediaDevices as any).getDisplayMedia(displayStreamOptions);
            } catch (e) {
                console.log('Could not capture system audio, will use microphone only:', e);
            }

            // Create audio context and mixer
            const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
            audioContextRef.current = new AudioContextConstructor();
            const audioContext = audioContextRef.current;
            const destination = audioContext.createMediaStreamDestination();

            // Mix microphone audio
            const micSource = audioContext.createMediaStreamSource(micStream);
            micSource.connect(destination);

            // Mix system audio if available
            let systemSource: MediaStreamAudioSourceNode | null = null;
            if (systemStream && systemStream.getAudioTracks().length > 0) {
                systemSource = audioContext.createMediaStreamSource(systemStream);
                systemSource.connect(destination);
            }

            // Set up analyzer for visualization
            analyserRef.current = audioContext.createAnalyser();
            micSource.connect(analyserRef.current);
            if (systemSource) {
                systemSource.connect(analyserRef.current);
            }
            analyserRef.current.fftSize = 256;

            visualizeAudio();

            // Record the mixed stream
            const mixedStream = destination.stream;
            mediaRecorderRef.current = new MediaRecorder(mixedStream);
            mediaRecorderRef.current.start();

            // Store references to stop later
            (mediaRecorderRef.current as any).originalStreams = [micStream, systemStream].filter(Boolean);

            // Use speech recognition on microphone (it doesn't work on system audio)
            // Note: Speech Recognition API only transcribes microphone input.
            // System audio is recorded in mixedStream but not transcribed live.
            // The mixed audio recording includes both mic and system audio for playback.
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.start();
                } catch (e) {
                    console.warn("Speech recognition already started", e);
                }
            }

            // Show info to user about system audio
            if (systemStream) {
                console.log('✓ Capturing both microphone and system audio. Live transcription shows your voice only. Full audio is being recorded.');
                setSystemAudioCaptured(true);
            } else {
                console.log('✓ Capturing microphone only. For meeting audio, select "Share tab audio" when prompted.');
                setSystemAudioCaptured(false);
            }

            setIsRecording(true);
            setError(null);
        } catch (err) {
            console.error('Error accessing audio:', err);
            setError('Could not access audio. Please check permissions.');
        }
    };

    const stopRecording = () => {
        setIsRecording(false);
        saveCurrentTranscription(); // Final save

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();

            // Stop mixed stream tracks
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());

            // Stop original streams (microphone and system audio)
            const originalStreams = (mediaRecorderRef.current as any).originalStreams;
            if (originalStreams) {
                originalStreams.forEach((stream: MediaStream) => {
                    if (stream) {
                        stream.getTracks().forEach(track => track.stop());
                    }
                });
            }
        }

        if (recognitionRef.current) {
            recognitionRef.current.stop();
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
        }

        // Increment segment offset for next recording session
        segmentOffsetRef.current += 1;

        setAudioLevel(new Array(5).fill(4));
        setSystemAudioCaptured(false);
    };

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    const handleBotJoin = async () => {
        if (!botUrl) return;

        setBotStatus('joining');
        setToast({ message: 'Connecting to local bot...', type: 'info' });

        // Connect to local bot server
        const socket = io('http://localhost:3001');
        socketRef.current = socket;

        socket.on('connect', () => {
            console.log('Connected to bot server');
            socket.emit('join-meeting', { url: botUrl });
        });

        socket.on('bot-status', (data: { status: string, message: string }) => {
            console.log('Bot status:', data);
            if (data.status === 'joined') {
                setBotStatus('connected');
                setToast({ message: 'Bot joined meeting! Waiting for captions...', type: 'success' });

                // Add system message
                setModeData(prev => ({
                    ...prev,
                    bot: {
                        ...prev.bot,
                        transcript: [
                            {
                                id: 'bot-welcome',
                                speaker: 'System',
                                text: 'Bot joined. Capturing live captions from the meeting...',
                                timestamp: Date.now(),
                                isFinal: true
                            }
                        ]
                    }
                }));
            } else if (data.status === 'waiting_for_entry') {
                setToast({ message: data.message, type: 'info' });
            }
        });

        socket.on('bot-error', (data: { message: string }) => {
            console.error('Bot error:', data.message);
            setToast({ message: `Bot Error: ${data.message}`, type: 'error' });
            setBotStatus('idle');
        });

        socket.on('transcript-update', (data: { speaker: string, text: string, timestamp: number }) => {
            setModeData(prev => {
                const newSegment = {
                    id: `bot-seg-${Date.now()}`,
                    speaker: data.speaker,
                    text: data.text,
                    timestamp: data.timestamp,
                    isFinal: true
                };

                const newData = {
                    ...prev,
                    bot: {
                        ...prev.bot,
                        transcript: [...prev.bot.transcript, newSegment]
                    }
                };
                saveCompleteData(newData);
                return newData;
            });
        });
    };

    const handleBotLeave = () => {
        if (socketRef.current) {
            socketRef.current.emit('leave-meeting');
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        setBotStatus('completed');
        setToast({ message: 'Bot left the meeting', type: 'info' });

        // Add system message
        setModeData(prev => {
            const newData = {
                ...prev,
                bot: {
                    ...prev.bot,
                    transcript: [
                        ...prev.bot.transcript,
                        {
                            id: 'bot-left',
                            speaker: 'System',
                            text: 'Bot disconnected.',
                            timestamp: Date.now(),
                            isFinal: true
                        }
                    ]
                }
            };
            saveCompleteData(newData);
            return newData;
        });
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        // Validate file type
        if (!['txt', 'srt', 'vtt', 'pdf', 'docx'].includes(fileExtension || '')) {
            setToast({ message: 'Supported formats: .txt, .srt, .vtt, .pdf, .docx', type: 'error' });
            return;
        }

        // Show processing message
        setModeData(prev => ({
            ...prev,
            upload: {
                ...prev.upload,
                transcript: [
                    {
                        id: 'upload-processing',
                        speaker: 'System',
                        text: `Processing ${fileExtension?.toUpperCase()} file: ${file.name}...`,
                        timestamp: Date.now(),
                        isFinal: true
                    }
                ]
            }
        }));

        // Process file based on type
        try {
            let transcriptText = '';

            if (fileExtension === 'txt') {
                // Read text file directly
                transcriptText = await file.text();
            } else if (fileExtension === 'srt') {
                // Read SRT subtitle file and extract text
                const srtContent = await file.text();
                // Remove subtitle numbers, timestamps, and extra newlines
                transcriptText = srtContent
                    .replace(/^\d+\s*$/gm, '') // Remove subtitle numbers
                    .replace(/\d{2}:\d{2}:\d{2},\d{3}\s*-->\s*\d{2}:\d{2}:\d{2},\d{3}/g, '') // Remove timestamps
                    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
                    .trim();
            } else if (fileExtension === 'vtt') {
                // Read VTT (WebVTT) subtitle file and extract text
                const vttContent = await file.text();
                // Remove WEBVTT header, cue identifiers, timestamps, and extra newlines
                transcriptText = vttContent
                    .replace(/^WEBVTT.*$/gm, '') // Remove WEBVTT header
                    .replace(/^NOTE.*$/gm, '') // Remove NOTE lines
                    .replace(/^\d+$/gm, '') // Remove cue numbers
                    .replace(/\d{2}:\d{2}:\d{2}\.\d{3}\s*-->\s*\d{2}:\d{2}:\d{2}\.\d{3}/g, '') // Remove timestamps
                    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
                    .trim();
            } else if (fileExtension === 'pdf') {
                // Extract text from PDF using pdf.js
                try {
                    const arrayBuffer = await file.arrayBuffer();

                    // Disable worker to avoid CORS and loading issues
                    console.log('Loading PDF document...');
                    const loadingTask = pdfjsLib.getDocument({
                        data: arrayBuffer,
                        useWorkerFetch: false,
                        isEvalSupported: false,
                        useSystemFonts: true
                    });
                    const pdf = await loadingTask.promise;
                    console.log('PDF loaded, pages:', pdf.numPages);

                    let fullText = '';

                    // Extract text from each page
                    for (let i = 1; i <= pdf.numPages; i++) {
                        console.log(`Extracting page ${i}...`);
                        const page = await pdf.getPage(i);
                        const textContent = await page.getTextContent();
                        const pageText = textContent.items
                            .map((item: any) => item.str)
                            .join(' ');
                        fullText += pageText + '\n\n';
                    }

                    transcriptText = fullText.trim();
                    console.log('PDF text extracted, length:', transcriptText.length);

                    if (!transcriptText) {
                        throw new Error('No text content found in PDF');
                    }
                } catch (error) {
                    console.error('Error extracting PDF text:', error);
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    console.error('Error details:', errorMessage);
                    setToast({ message: `Failed to extract text from PDF: ${errorMessage}`, type: 'error' });
                    return;
                }
            } else if (fileExtension === 'docx') {
                // Extract text from DOCX using mammoth
                try {
                    const arrayBuffer = await file.arrayBuffer();
                    const result = await mammoth.extractRawText({ arrayBuffer });
                    transcriptText = result.value.trim();

                    if (!transcriptText) {
                        throw new Error('No text content found in DOCX');
                    }

                    if (result.messages && result.messages.length > 0) {
                        console.warn('DOCX conversion warnings:', result.messages);
                    }
                } catch (error) {
                    console.error('Error extracting DOCX text:', error);
                    setToast({ message: 'Failed to extract text from DOCX file', type: 'error' });
                    return;
                }
            } else if (fileExtension === 'mp3' || fileExtension === 'mp4') {
                // Audio/video transcription requires backend API
                setToast({ message: 'Audio/video transcription requires backend service. Please use text-based files.', type: 'error' });
                return;
            }

            // Split text into segments (by lines or paragraphs)
            const lines = transcriptText.split('\n').filter(line => line.trim());
            const segments = lines.map((line, index) => ({
                id: `upload-segment-${index}`,
                speaker: `Speaker ${(index % 3) + 1}`,
                text: line,
                timestamp: Date.now() + (index * 1000),
                isFinal: true
            }));

            setModeData(prev => {
                const newData = {
                    ...prev,
                    upload: {
                        ...prev.upload,
                        transcript: segments.length > 0 ? segments : [
                            {
                                id: 'upload-empty',
                                speaker: 'System',
                                text: 'File appears to be empty or could not be processed.',
                                timestamp: Date.now(),
                                isFinal: true
                            }
                        ]
                    }
                };

                // Save complete data to DB immediately
                saveCompleteData(newData);

                return newData;
            });

            setToast({ message: 'File uploaded and processed successfully', type: 'success' });
        } catch (error) {
            console.error('Error processing file:', error);
            setModeData(prev => ({
                ...prev,
                upload: {
                    ...prev.upload,
                    transcript: [
                        {
                            id: 'upload-error',
                            speaker: 'System',
                            text: 'Error processing file. Please try again.',
                            timestamp: Date.now(),
                            isFinal: true
                        }
                    ]
                }
            }));
            setToast({ message: 'Error processing file', type: 'error' });
        }

        // Reset file input
        e.target.value = '';
    };

    const handleClearTranscript = () => {
        if (mode === 'select') return;

        setModeData(prev => {
            const newData = {
                ...prev,
                [mode]: {
                    ...prev[mode],
                    transcript: []
                }
            };
            saveCompleteData(newData);
            return newData;
        });

        setToast({ message: 'Transcription cleared', type: 'info' });
    };

    if (mode === 'select') {
        return (
            <div className="in-meeting-assistant">
                <div className="mode-selection">
                    <h2 className="mode-title">Choose how to capture this meeting</h2>
                    <div className="mode-cards">
                        <div className="mode-card" onClick={() => setMode('manual')}>
                            <div className="mode-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                    <line x1="12" y1="19" x2="12" y2="23" />
                                    <line x1="8" y1="23" x2="16" y2="23" />
                                </svg>
                            </div>
                            <h3>Offline Recording</h3>
                            <p>Record and transcribe in-person discussions using your microphone.</p>
                        </div>

                        <div className="mode-card" onClick={() => setMode('upload')}>
                            <div className="mode-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <h3>Upload File</h3>
                            <p>Upload an existing audio or video file for transcription.</p>
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="in-meeting-assistant">
            <div className="assistant-header">
                <div className="recording-status">
                    <button className="back-to-modes" onClick={() => setMode('select')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                        Back
                    </button>
                    {mode === 'manual' && (
                        <>
                            <div className={`status-dot ${isRecording ? 'recording' : ''}`} />
                            <span className="timer">{formatTime(currentDuration)}</span>
                            <button
                                className={`header-record-btn ${isRecording ? 'recording' : ''}`}
                                onClick={toggleRecording}
                                title={isRecording ? "Stop Recording" : "Start Recording"}
                            >
                                <div className="record-icon-wrapper">
                                    {isRecording ? (
                                        <div className="stop-icon" />
                                    ) : (
                                        <div className="record-icon" />
                                    )}
                                </div>
                                <span className="record-text">{isRecording ? 'Stop' : 'Record'}</span>
                            </button>
                            {isRecording && (
                                <div className="audio-visualizer">
                                    {audioLevel.map((height, i) => (
                                        <div
                                            key={i}
                                            className="audio-bar"
                                            style={{
                                                height: `${height}%`,
                                                animationDuration: `${0.5 + Math.random() * 0.5}s`
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    {(mode === 'manual' || mode === 'upload') && (
                        <>
                            <button
                                className="convert-btn"
                                onClick={handleConvertToNotes}
                                disabled={isConverting || currentTranscript.length === 0}
                                title="Convert transcription to summary, highlights, and action items using AI"
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
                                </svg>
                                <span>{isConverting ? 'Converting...' : 'Generate Notes'}</span>
                            </button>
                        </>
                    )}
                </div>
                <div className="header-actions">
                    {isSaving && <span className="saving-indicator">Saving...</span>}
                    {error && <span className="error-message" style={{ color: '#ef4444', fontSize: '0.9rem' }}>{error}</span>}
                    {isRecording && systemAudioCaptured && (
                        <span className="info-message" style={{ color: '#10b981', fontSize: '0.85rem', marginLeft: '8px' }}>
                            ✓ Recording mic + system audio
                        </span>
                    )}
                    {isRecording && !systemAudioCaptured && (
                        <span className="info-message" style={{ color: '#f59e0b', fontSize: '0.85rem', marginLeft: '8px' }}>
                            ⓘ Recording mic only (restart and share tab audio for full capture)
                        </span>
                    )}
                </div>
            </div>

            <div className="assistant-content">
                {mode === 'upload' && currentTranscript.length === 0 ? (
                    <div className="upload-container">
                        <label className="upload-zone">
                            <input type="file" hidden accept=".txt,.srt,.vtt,.pdf,.docx" onChange={handleFileUpload} />
                            <div className="upload-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                            </div>
                            <div className="upload-text">Upload meeting files</div>
                            <div className="upload-subtext">Text files, subtitles, or documents (.txt, .srt, .vtt, .pdf, .docx)</div>
                        </label>
                    </div>
                ) : (
                    <div className="transcript-panel">
                        <div className="panel-header">
                            <span className="panel-title">
                                {mode === 'manual' ? 'Live Transcript' : 'File Transcript'}
                            </span>
                            {mode === 'upload' && currentTranscript.length > 0 && (
                                <button className="clear-transcript-btn" onClick={handleClearTranscript} title="Clear transcription">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="transcript-stream">
                            {currentTranscript.length === 0 ? (
                                <div className="empty-state" style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '40px' }}>
                                    <p>
                                        {mode === 'manual' ? 'Start recording to see live transcription...' : 'Waiting for content...'}
                                    </p>
                                </div>
                            ) : (
                                currentTranscript.map((segment, index) => (
                                    <div key={segment.id} className="transcript-segment">
                                        <div className="segment-content">
                                            <div className={`segment-text ${!segment.isFinal ? 'interim' : ''}`}>
                                                <span className="segment-timestamp">[{new Date(segment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}]</span> {segment.text}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <div className="notes-panel">
                    <div className="panel-header">
                        <span className="panel-title">Meeting Notes</span>
                    </div>

                    <div className="notes-sections">
                        <div className="note-section">
                            <div className="section-label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                    <polyline points="14 2 14 8 20 8" />
                                    <line x1="16" y1="13" x2="8" y2="13" />
                                    <line x1="16" y1="17" x2="8" y2="17" />
                                    <polyline points="10 9 9 9 8 9" />
                                </svg>
                                Summary
                            </div>
                            <textarea
                                className="section-textarea summary"
                                placeholder="Summarize the key points of the meeting..."
                                value={currentNotes.summary}
                                onChange={(e) => updateNoteSection('summary', e.target.value, e)}
                            />
                        </div>

                        <div className="note-section">
                            <div className="section-label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                                Highlights
                            </div>
                            <textarea
                                className="section-textarea highlights"
                                placeholder="Key highlights and important moments..."
                                value={currentNotes.highlights}
                                onChange={(e) => updateNoteSection('highlights', e.target.value, e)}
                            />
                        </div>

                        <div className="note-section">
                            <div className="section-label">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 11 12 14 22 4" />
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                                </svg>
                                Action Items
                            </div>
                            <textarea
                                className="section-textarea actions"
                                placeholder="Tasks and follow-ups..."
                                value={currentNotes.actionItems}
                                onChange={(e) => updateNoteSection('actionItems', e.target.value, e)}
                            />
                            <button
                                className="move-action-items-btn"
                                onClick={handleMoveActionItemsToPost}
                                disabled={!currentNotes.actionItems || currentNotes.actionItems.trim().length === 0}
                                title="Move action items to Post-Meeting tab"
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19" />
                                    <polyline points="19 12 12 19 5 12" />
                                </svg>
                                Move to Post-Meeting
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div >
    );
};

export default InMeetingAssistant;
